import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import StreamCard from './components/StreamCard';
import ListenerDetails from './components/ListenerDetails';
import LoginForm from './components/LoginForm';
import HistoricalDashboard from './components/HistoricalDashboard';
import ListenerSummary from './components/ListenerSummary';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import RealTimeWebSocket from './components/RealTimeWebSocket';
import './App.css';

// Utility function to format timestamps in Dublin timezone
const formatDublinTime = (timestamp) => {
  if (!timestamp) return 'Unknown';
  
  try {
    // Try parsing as ISO string or regular date string
    const date = new Date(timestamp);
    
    // If invalid date, return original
    if (isNaN(date.getTime())) return timestamp;
    
    return date.toLocaleString("en-IE", {
      timeZone: "Europe/Dublin",
      year: 'numeric',
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (error) {
    console.error('Error formatting timestamp:', timestamp, error);
    return timestamp;
  }
};

function App() {
  // Cache bust - VERSION 2.0 - FINAL FIX DEPLOYED
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [streams, setStreams] = useState([]);
  const [listeners, setListeners] = useState([]);
  const [selectedStream, setSelectedStream] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [currentView, setCurrentView] = useState('realtime');
  // Removed loading state - data updates smoothly without overlays
  const [loginLoading, setLoginLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loginError, setLoginError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [nginxAnalytics, setNginxAnalytics] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [wsConnectedStable, setWsConnectedStable] = useState(false);
  const [realTimeData, setRealTimeData] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);


  // Handle burger menu toggle
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  // Handle menu item click
  const handleMenuItemClick = (view) => {
    setCurrentView(view);
    setMenuOpen(false); // Close menu after selection
  };

  // Handle logout from burger menu
  const handleLogoutFromMenu = () => {
    setMenuOpen(false);
    handleLogout();
  };

  // Set up axios interceptor for authentication
  useEffect(() => {
    if (credentials) {
      // Add request interceptor to include authentication
      const interceptor = axios.interceptors.request.use(
        (config) => {
          if (credentials && config.url?.startsWith('/api/')) {
            const auth = btoa(`${credentials.username}:${credentials.password}`);
            config.headers.Authorization = `Basic ${auth}`;
          }
          return config;
        },
        (error) => {
          return Promise.reject(error);
        }
      );

      // Cleanup interceptor when credentials change
      return () => {
        axios.interceptors.request.eject(interceptor);
      };
    }
  }, [credentials]);

  // Check for stored credentials on app startup
  useEffect(() => {
    const storedCredentials = localStorage.getItem('icecast_credentials');
    if (storedCredentials) {
      try {
        const parsedCredentials = JSON.parse(storedCredentials);
        setCredentials(parsedCredentials);
        setIsAuthenticated(true);
        console.log('Auto-authenticated from stored credentials');
      } catch (error) {
        console.error('Error parsing stored credentials:', error);
        localStorage.removeItem('icecast_credentials');
      }
    }
  }, []);

  // Handle login
  const handleLogin = async (loginCredentials) => {
    setLoginLoading(true);
    setLoginError(null);

    try {
      const response = await axios.post('/api/login', loginCredentials);
      
      if (response.data.success) {
        setCredentials(loginCredentials);
        setIsAuthenticated(true);
        setLoginError(null);
        
        // Store credentials in localStorage for session persistence
        localStorage.setItem('icecast_credentials', JSON.stringify(loginCredentials));
        
        // Use initial data from login response for instant display
        if (response.data.data) {
          setStreams(response.data.data.streams || []);
          setListeners(response.data.data.listeners || []);
          setLastUpdated(response.data.data.lastUpdated);
        }
      } else {
        setLoginError(response.data.error || 'Authentication failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.response?.data?.error) {
        setLoginError(err.response.data.error);
      } else if (err.code === 'ECONNREFUSED') {
        setLoginError('Cannot connect to server. Please check if the Icecast server is running.');
      } else {
        setLoginError('Connection failed. Please verify your server details and credentials.');
      }
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    setCredentials(null);
    setStreams([]);
    setListeners([]);
    setSelectedStream(null);
    setError(null);
    setLastUpdated(null);
    setNginxAnalytics(null);
    
    // Clear stored credentials
    localStorage.removeItem('icecast_credentials');
  };

  // Fetch data from API (only when authenticated) - optimized to prevent unnecessary re-renders
  const fetchData = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      // Data updates seamlessly without loading overlay
      const response = await axios.get('/api/status');
      
      // Handle the improved error response structure from server
      if (response.data) {
        // Check if we got an error but with cached data
        if (response.data.error && response.data.errorDetails) {
          setError(`Warning: ${response.data.error}`);
          console.warn('Using cached data due to fetch error:', response.data.errorDetails);
        } else {
          setError(null);
        }
        
        // Update streams and listeners if they exist in response
        if (response.data.streams !== undefined) {
          setStreams(response.data.streams);
        }
        if (response.data.listeners !== undefined) {
          // CRITICAL FIX: Filter out localhost/proxy connections from Icecast data
          const realListeners = response.data.listeners.filter(listener => {
            const ip = listener.ip || '';
            if (!ip || ip === '127.0.0.1' || ip === '::1' || ip === 'localhost') return false;
            if (ip.startsWith('192.168.') || ip.startsWith('10.') || ip.startsWith('169.254.')) return false;
            // Only filter private 172.16-31.x.x range (RFC 1918), not all 172.x.x.x
            if (ip.startsWith('172.')) {
              const secondOctet = parseInt(ip.split('.')[1], 10);
              if (secondOctet >= 16 && secondOctet <= 31) return false;
            }
            return true;
          });
          setListeners(realListeners);
        }
        if (response.data.lastUpdated) {
          setLastUpdated(response.data.lastUpdated);
        }
      }
    } catch (err) {
      // Only show error if we don't have existing data to fall back on
      if (streams.length === 0) {
        setError('Failed to fetch stream data. Connection may have been lost.');
      } else {
        setError('Connection issue detected. Showing last known data.');
      }
      console.error('Error fetching data:', err);
    } finally {
      // Smooth data updates - no loading state needed
    }
  }, [isAuthenticated, streams.length]);

  // Fetch nginx analytics data (backup for WebSocket) - optimized to prevent unnecessary re-renders
  const fetchNginxAnalytics = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await axios.get(`/api/current-listeners?t=${Date.now()}`);
      
      setNginxAnalytics(response.data);
      setLastUpdated(response.data.lastUpdated);
    } catch (error) {
      console.error('Error fetching nginx analytics:', error);
      // Don't clear existing data on error
    }
  }, [isAuthenticated]);

  // Auto-refresh every 10 seconds (only when authenticated)
  useEffect(() => {
    if (!isAuthenticated) return;

    // Initial fetch
    fetchData();

    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchData]);

  // Synchronized polling for nginx analytics
  useEffect(() => {
    if (!isAuthenticated) return;

    // Initial fetch
    fetchNginxAnalytics();
    
    // Refresh every 10 seconds to match main data polling
    const fastInterval = setInterval(fetchNginxAnalytics, 10000);

    return () => {
      clearInterval(fastInterval);
    };
  }, [isAuthenticated, fetchNginxAnalytics]);

  // Handle real-time WebSocket updates from Icecast API
  const handleListenerUpdate = useCallback((data) => {
    // Simple, reliable state updates
    setRealTimeData(data);
    setLastUpdated(data.lastUpdated);
    
    // Update state with real-time Icecast data - FILTER OUT PROXY IPs
    if (data.listeners) {
      // CRITICAL FIX: Filter out localhost/proxy connections from Icecast data
      // These are nginx→icecast internal connections, not real listeners
      const realListeners = data.listeners.filter(listener => {
        const ip = listener.ip || '';
        if (!ip || ip === '127.0.0.1' || ip === '::1' || ip === 'localhost') return false;
        if (ip.startsWith('192.168.') || ip.startsWith('10.') || ip.startsWith('169.254.')) return false;
        // Only filter private 172.16-31.x.x range (RFC 1918), not all 172.x.x.x
        if (ip.startsWith('172.')) {
          const secondOctet = parseInt(ip.split('.')[1], 10);
          if (secondOctet >= 16 && secondOctet <= 31) return false;
        }
        return true;
      });
      
      setListeners(realListeners);
    }
    
    // Update streams data (Icecast stream info only)
    if (data.streams) {
      setStreams(data.streams);
    }
    
    // DO NOT UPDATE nginxAnalytics HERE!
    // nginxAnalytics should ONLY be updated by HTTP polling from /api/current-listeners
    // WebSocket provides Icecast data (which only sees proxy count), not real nginx analytics
  }, []);

  const handleConnectionStatus = useCallback((connected) => {
    setWsConnected(connected);
    console.log(`WebSocket connection status: ${connected ? 'Connected' : 'Disconnected'}`);
  }, []);

  // Debounce WebSocket connection status to prevent flickering
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setWsConnectedStable(wsConnected);
    }, wsConnected ? 500 : 2000); // Wait 500ms for connection, 2s for disconnection

    return () => clearTimeout(debounceTimer);
  }, [wsConnected]); // Force cache invalidation v3 - new build timestamp: 2025-09-15

  // Get listeners for selected stream - use consistent logic with totalListeners
  const getStreamListeners = (streamMount) => {
    // Use the same prioritization logic as totalListeners for consistency
    
    // Priority 1: If we have streams data, calculate from stream count
    const matchingStream = streams.find(s => s.mount === streamMount || s.mount?.includes(streamMount?.replace('/radio', '')));
    if (matchingStream && matchingStream.listeners) {
      // Create placeholder listeners based on stream count for display consistency
      const count = parseInt(matchingStream.listeners) || 0;
      return Array(count).fill(null).map((_, i) => ({
        id: `stream_listener_${i}`,
        ip: '127.0.0.1', // Localhost due to proxy
        mount: streamMount,
        connected_seconds: 0,
        user_agent: 'Stream Listener',
        isPlaceholder: true
      }));
    }
    
    // Priority 2: Use nginx analytics if available (server already filters localhost)
    if (nginxAnalytics?.listeners) {
      return nginxAnalytics.listeners.filter(listener => 
        listener.mount === streamMount || 
        (streamMount && streamMount.includes('radio') && listener.mount?.includes('radio'))
      );
    }
    
    // Priority 3: Fallback to raw listeners data
    return listeners.filter(listener => 
      listener.mount === streamMount || listener.mount?.includes(streamMount)
    );
  };

  // Handle stream selection - memoized to prevent unnecessary re-renders
  const handleStreamSelect = useCallback((stream) => {
    try {
      console.log('🎯 Stream selected:', stream);
      setSelectedStream(prevSelected => 
        prevSelected?.mount === stream.mount ? null : stream
      );
      console.log('✅ Stream selection completed');
    } catch (error) {
      console.error('❌ Error in handleStreamSelect:', error);
    }
  }, []);

  // Format last updated time
  const formatLastUpdated = (timestamp) => {
    if (!timestamp) return 'Never';
    return formatDublinTime(timestamp);
  };

  // ENFORCED SINGLE SOURCE - NO MORE INCONSISTENCIES
  const SINGLE_LISTENER_COUNT = useMemo(() => {
    // FORCE ALL COMPONENTS TO USE THIS EXACT VALUE
    let count = 0;
    
    // Priority 1: Use nginx analytics currentListeners (from /api/current-listeners - most accurate)
    if (nginxAnalytics?.currentListeners !== undefined && nginxAnalytics.currentListeners !== null) {
      count = nginxAnalytics.currentListeners;
    }
    // Priority 2: Use nginx analytics listeners array (if available)
    else if (nginxAnalytics?.listeners && nginxAnalytics.listeners.length > 0) {
      const realListeners = nginxAnalytics.listeners.filter(l => 
        l.ip && l.ip !== '127.0.0.1' && l.ip !== 'localhost' && l.ip !== '::1'
      );
      count = realListeners.length;
    }
    // Priority 3: Use filtered icecast listeners count (WebSocket data)
    else if (listeners && listeners.length > 0) {
      const realListeners = listeners.filter(l => 
        l.ip && l.ip !== '127.0.0.1' && l.ip !== 'localhost' && l.ip !== '::1'
      );
      count = realListeners.length;
    }
    // NO Priority 4! Do NOT fall back to streams[].listeners
    // That value is always 1 (the nginx→icecast proxy connection), not real users
    // If we don't have nginx analytics data, show 0 instead of misleading proxy count
    else {
      count = 0;
    }
    
    return count;
  }, [nginxAnalytics, listeners]);

  // Legacy variable for compatibility
  const { totalListeners } = { totalListeners: SINGLE_LISTENER_COUNT };

  // Memoize filtered streams to prevent unnecessary re-renders
  const filteredStreams = useMemo(() => 
    streams.filter(stream => 
      stream.title.toLowerCase() !== 'test' && 
      !stream.mount.toLowerCase().includes('test') &&
      !stream.mount.toLowerCase().includes('320') &&
      (stream.mount.includes('192') || stream.mount.toLowerCase().includes('lounge'))
    )
  , [streams]);

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="app">
        <LoginForm 
          onLogin={handleLogin}
          isLoading={loginLoading}
          error={loginError}
        />
      </div>
    );
  }

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-content authenticated">
          <div className="header-left">
            <div className="logo">
              <img src="/Play Logo Web Player.png" alt="PlayFM Logo" className="logo-image" />
            </div>
            <h1 className="site-title">PlayFm Stream Monitor</h1>
            
            {/* Burger Menu - Mobile Only */}
            <button 
              className={`burger-menu ${menuOpen ? 'open' : ''}`}
              onClick={toggleMenu}
              aria-label="Toggle navigation menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
          
          <div className="header-stats">
            <div className="stat-item">
              <span className="stat-value animated-stat">{streams.length}</span>
              <span className="stat-label">Active Streams</span>
            </div>
            <div className="stat-item">
              <span className="stat-value animated-stat">{SINGLE_LISTENER_COUNT}</span>
              <span className="stat-label">
                Live Listeners
                {nginxAnalytics && !wsConnectedStable && (
                  <span style={{ 
                    fontSize: '0.65rem', 
                    color: '#4ade80',
                    marginLeft: '0.3rem',
                    fontWeight: '500'
                  }}>
Real IPs
                  </span>
                )}
              </span>
            </div>
            <div className="connection-status">
              <div className={`status-indicator ${wsConnectedStable ? 'live' : 'polling'}`}></div>
              {wsConnectedStable ? 'Live Updates Connected' : 'Polling Mode'}
            </div>
            <div className="last-updated">
              Last updated: {formatLastUpdated(lastUpdated)}
            </div>
          </div>
          
        </div>
      </header>

      {/* Desktop Navigation */}
      <div className="navigation-container">
        <div className="navigation-content">
          <div className="nav-tabs">
            <button 
              className={`nav-tab ${currentView === 'realtime' ? 'active' : ''}`}
              onClick={() => setCurrentView('realtime')}
            >
              Live Dashboard
            </button>
            <button 
              className={`nav-tab ${currentView === 'historical' ? 'active' : ''}`}
              onClick={() => setCurrentView('historical')}
            >
              Historical Analytics
            </button>
            <button 
              className={`nav-tab ${currentView === 'analytics' ? 'active' : ''}`}
              onClick={() => setCurrentView('analytics')}
            >
              Real-Time Analytics
            </button>
          </div>
          <button 
            className="logout-button"
            onClick={handleLogout}
            title="Disconnect from Icecast server"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Mobile Slide-Out Menu */}
      {menuOpen && <div className="menu-overlay" onClick={toggleMenu}></div>}
      <nav className={`slide-menu ${menuOpen ? 'open' : ''}`}>
        <div className="menu-header">
          <h3>Navigation</h3>
          <button className="menu-close" onClick={toggleMenu} aria-label="Close menu">
            ×
          </button>
        </div>
        <div className="menu-items">
          <button 
            className={`menu-item ${currentView === 'realtime' ? 'active' : ''}`}
            onClick={() => handleMenuItemClick('realtime')}
          >
            <span className="menu-icon">🏠</span>
            Live Dashboard
          </button>
          <button 
            className={`menu-item ${currentView === 'historical' ? 'active' : ''}`}
            onClick={() => handleMenuItemClick('historical')}
          >
            <span className="menu-icon">📊</span>
            Historical Analytics
          </button>
          <button 
            className={`menu-item ${currentView === 'analytics' ? 'active' : ''}`}
            onClick={() => handleMenuItemClick('analytics')}
          >
            <span className="menu-icon">⚡</span>
            Real-Time Analytics
          </button>
          <div className="menu-divider"></div>
          <button 
            className="menu-item logout-item"
            onClick={handleLogoutFromMenu}
            title="Disconnect from Icecast server"
          >
            <span className="menu-icon">🚪</span>
            Logout
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="main-content">
        {currentView === 'realtime' && (
          <>
            {error && (
              <div className={`error ${error.includes('Warning') ? 'warning' : ''}`}>
                {error}
              </div>
            )}

            {/* Smooth data updates - no loading overlay needed */}

            {/* Listener Summary - ENFORCED SINGLE SOURCE */}
            <ListenerSummary 
              currentListeners={SINGLE_LISTENER_COUNT}
              isRealIPData={!!nginxAnalytics}
              wsConnected={wsConnectedStable}
              totalListeners={SINGLE_LISTENER_COUNT}
              nginxAnalytics={nginxAnalytics}
              ENFORCED_COUNT={SINGLE_LISTENER_COUNT}
            />

            {/* Streams Grid */}
            <div className="streams-grid">
              {streams.length === 0 ? (
                <div className="no-streams">
                  {(
                    <p>No active streams found. Make sure your Icecast server is running and accessible.</p>
                  )}
                </div>
              ) : (
                filteredStreams.map((stream, index) => (
                  <StreamCard
                    key={stream.mount || index}
                    stream={stream}
                    isSelected={selectedStream?.mount === stream.mount}
                    onClick={() => handleStreamSelect(stream)}
                    listenerCount={SINGLE_LISTENER_COUNT}
                  />
                ))
              )}
            </div>

            {/* Listener Details */}
            {selectedStream && (
              <ListenerDetails
                stream={selectedStream}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                nginxAnalytics={nginxAnalytics}
                ENFORCED_COUNT={SINGLE_LISTENER_COUNT}
                icecastListeners={listeners}
              />
            )}
          </>
        )}

        {currentView === 'historical' && (
          <HistoricalDashboard />
        )}

        {currentView === 'analytics' && (
          <AnalyticsDashboard analyticsData={nginxAnalytics} />
        )}
      </main>

      {/* WebSocket connection status indicator */}
      {isAuthenticated && (
        <div className={`websocket-status ${wsConnectedStable ? 'connected' : 'disconnected'}`}>
          <div className="status-dot"></div>
          {wsConnectedStable ? 'Live Updates Active' : 'Reconnecting...'}
        </div>
      )}

      {/* Real-time WebSocket connection for Icecast API updates */}
      {isAuthenticated && (
        <RealTimeWebSocket 
          onListenerUpdate={handleListenerUpdate}
          onConnectionStatus={handleConnectionStatus}
        />
      )}
    </div>
  );
}

export default App;// Cache bust - $(date)
